import { useEffect, useMemo, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ConditionField,
  MeasureDescriptor,
  NumberFormatterOptions,
} from "../../../../../../shared/reporting/api/biClient.types";
import { toAmountType, toCalculateBy } from "../../../../../../shared/reporting/utils/measureConverter";
import biClient from "../../../../../api/biApi";
import { ChartOfAccounts, MeasureDataset } from "../../../../../api/biApi.types";
import { useLocalization } from "../../../../../hooks/useLocalization";
import { selectAccountBookSettings } from "../../../../../store/selectors/clientSettingsSelectors";
import { selectMeasureDatasets } from "../../../../../store/selectors/measureDataSetsSelectors";
import {
  selectDimensions,
  selectFunctions,
  selectPostingChartOfAccounts,
} from "../../../../../store/selectors/metaDataSelectors";
import { createMeasureDatasetsAction, createMetaDataAction } from "../../../../../store/utils/storeUtils";
import { CustomMeasureState, initialState } from "../types/customMeasure.types";
import { CustomMeasureActionType } from "../types/customMeasureStateActions";
import { FormulaNode } from "../utilities/fillNodes";
import { INode } from "../utilities/formulaParser";
import { isFormulaValid } from "../utilities/formulaValid";
import { toFormulaNodes } from "../utilities/measureConverter";
import { Token } from "../utilities/tokenizer";
import { getAvailableDimensions } from "../utilities/variables.utils";
import { CustomMeasureReducer } from "./CustomMeasureReducer";

export type CustomMeasureReturnType = CustomMeasureState & {
  formulaValid: boolean;
  chartOfAccounts?: ChartOfAccounts;
  actions: {
    update: (changes: Partial<CustomMeasureState>) => void;
    setDataSets: (dataSets: MeasureDataset[]) => void;
    setRootNode: (rootNode?: INode) => void;
    updateFormulaNode: (node: FormulaNode, changes: Partial<FormulaNode>) => void;
    addConditions: (node: FormulaNode, newConditions: ConditionField[]) => void;
    updateCondition: (node: FormulaNode, condition: ConditionField, changes: Partial<ConditionField>) => void;
    removeCondition: (node: FormulaNode, condition: ConditionField) => void;
    highlightNode: (token: Token | undefined) => void;
    functionHovered: (token: Token | undefined) => void;
    setSelectedFormulaParameter: (token: Token | undefined) => void;
  };
  dataLoadingError: string | undefined;
  loading: boolean;
  datasetError: string | undefined;
};
interface Props {
  measure?: MeasureDescriptor;
  defaultGroup?: string;
  measureFormat?: NumberFormatterOptions | undefined;
}
export const useCustomMeasure = ({ measure, defaultGroup, measureFormat }: Props): CustomMeasureReturnType => {
  const dispatch = useDispatch();
  const [state, dispatchCustom] = useReducer(CustomMeasureReducer, initialState);
  const chartOfAccounts = useSelector(selectPostingChartOfAccounts);
  const accountingBookSettings = useSelector(selectAccountBookSettings);

  const formulaValid = useMemo(() => isFormulaValid(state), [state]);

  const datasetsState = useSelector(selectMeasureDatasets);
  const dimensions = useSelector(selectDimensions);
  const functions = useSelector(selectFunctions);
  const { custom_measure: locale } = useLocalization();

  const [dataLoadingError, setDataLoadingError] = useState<string>();
  const [datasetError, setDatasetError] = useState<string>();

  useEffect(() => {
    if (datasetsState.loaded) {
      actions.setDataSets(datasetsState.datasets);
      return;
    }
    dispatch(createMeasureDatasetsAction("update", { loading: true }));
    biClient
      .getMeasureDataSets()
      .then((result) => {
        if (result.success) {
          dispatch(createMeasureDatasetsAction("update", { datasets: result.data, loaded: true }));
          actions.setDataSets(result.data);
        } else {
          setDatasetError(locale.datasets_loading_error);
        }
      })
      .catch(() => setDatasetError(locale.datasets_loading_error))
      .finally(() => dispatch(createMeasureDatasetsAction("update", { loading: false })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chartOfAccounts === undefined) {
      dispatch(createMeasureDatasetsAction("update", { loading: true }));
      biClient
        .getChartOfAccounts()
        .then((result) => {
          if (result.success) {
            dispatch(createMetaDataAction("setChartOfAccounts", result.data));
          } else {
            setDataLoadingError(locale.chart_of_accounts_loading_error);
          }
        })
        .catch(() => setDataLoadingError(locale.chart_of_accounts_loading_error))
        .finally(() => dispatch(createMeasureDatasetsAction("update", { loading: false })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartOfAccounts]);

  useEffect(() => {
    const filteredDictionaries = getAvailableDimensions(dimensions, accountingBookSettings?.multiBook === true);

    dispatchCustom({
      type: CustomMeasureActionType.UPDATE_DIMENSIONS,
      payload: { dimensions: filteredDictionaries },
    });
  }, [accountingBookSettings?.multiBook, dimensions]);

  useEffect(() => {
    dispatchCustom({ type: CustomMeasureActionType.UPDATE, payload: { functions } });
  }, [functions]);

  useEffect(() => {
    let measureData: Partial<CustomMeasureState> = {};
    if (measureFormat) {
      measureData.format = measureFormat;
    }

    if (measure !== undefined) {
      measureData = {
        ...measureData,
        formula: measure.formula,
        caption: measure.caption,
        group: measure.group || defaultGroup,
        calculateBy: toCalculateBy(measure.units),
        formulaNodes: toFormulaNodes(measure.units, dimensions),
        amountType: toAmountType(measure.units),
        isConfigurable: measure.capabilities.isConfigurable,
      };
    } else {
      measureData = { ...measureData, group: defaultGroup };
    }

    dispatchCustom({ type: CustomMeasureActionType.UPDATE, payload: measureData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, defaultGroup, measureFormat]);

  const actions = useMemo(() => {
    return {
      update: (changes: Partial<CustomMeasureState>) =>
        dispatchCustom({ type: CustomMeasureActionType.UPDATE, payload: changes }),
      setDataSets: (dataSets: MeasureDataset[]) =>
        dispatchCustom({ type: CustomMeasureActionType.SET_DATASETS, payload: dataSets }),
      setRootNode: (rootNode?: INode) =>
        dispatchCustom({ type: CustomMeasureActionType.SET_ROOTNODE, payload: rootNode }),
      updateFormulaNode: (node: FormulaNode, changes: Partial<FormulaNode>) =>
        dispatchCustom({ type: CustomMeasureActionType.UPDATE_FORMULA_NODE, payload: { node, changes } }),
      addConditions: (node: FormulaNode, newConditions: ConditionField[]) =>
        dispatchCustom({ type: CustomMeasureActionType.ADD_CONDITIONS, payload: { node, newConditions } }),
      updateCondition: (node: FormulaNode, condition: ConditionField, changes: Partial<ConditionField>) =>
        dispatchCustom({
          type: CustomMeasureActionType.UPDATE_CONDITION,
          payload: { node, condition, changes },
        }),
      removeCondition: (node: FormulaNode, condition: ConditionField) =>
        dispatchCustom({ type: CustomMeasureActionType.REMOVE_CONDITION, payload: { node, condition } }),
      highlightNode: (token: Token | undefined) =>
        dispatchCustom({ type: CustomMeasureActionType.HIGHLIGHT_NODE, payload: token }),
      functionHovered: (token: Token | undefined) =>
        dispatchCustom({ type: CustomMeasureActionType.FUNCTION_HOVERED, payload: token }),
      setSelectedFormulaParameter: (token: Token | undefined) =>
        dispatchCustom({ type: CustomMeasureActionType.FORMULA_PARAMETER_CLICK, payload: token }),
    };
  }, []);

  return {
    ...state,
    formulaValid,
    chartOfAccounts,
    actions,
    dataLoadingError,
    loading: datasetsState.loading,
    datasetError,
  };
};
